import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { ChevronDown, EllipsisVertical, MessageSquareText, Repeat, ShieldCheck, UserMinus } from "lucide-react";
import { Avatar } from "@/modules/escala/components/Avatar";
import { Pill } from "@/modules/escala/components/Pill";
import { rotuloResposta } from "@/modules/escala/format";
import type { ParticipanteDetalhe } from "@/modules/escala/services/escalaService";

interface CardParticipanteProps {
  participante: ParticipanteDetalhe;
  /**
   * Ações de gestão (DEC-02: só quem lidera/gerencia o ministério dono — a RPC
   * também recusa se a leitura do cliente estiver errada). O menu ⋯ só aparece
   * se pelo menos uma estiver presente; quem decide quais existem é a página.
   */
  onAlterarFuncao?: () => void;
  onRegistrarResposta?: () => void;
  onRemover?: () => void;
}

/**
 * Um participante na equipe atual da escala. Mostra o selo de resposta com
 * os 3+1 estados (PENDENTE / CONFIRMADA própria / CONFIRMADA automática /
 * CONFIRMADA ou RECUSADA administrativa) — nunca decide localmente qual é o
 * estado real, só reflete `resposta`/`origem_resposta` como o backend gravou.
 *
 * As ações ficam num menu ⋯ (opção B da proposta de UX): "Alterar função",
 * "Registrar resposta" e "Remover da escala" (esta em vermelho, separada, e
 * continua pedindo confirmação na página).
 *
 * Justificativa da recusa (S-2, ESC-DEC-006): item recolhível, fechado por
 * padrão. Só aparece quando o banco liberou o status deste participante
 * (`respostaVisivel`) e a resposta é RECUSADA com justificativa — o banco já
 * entrega a justificativa apenas à própria pessoa, a líder/gestor do
 * ministério dono, ao administrador e à visão ampla.
 */
export function CardParticipante({
  participante,
  onAlterarFuncao,
  onRegistrarResposta,
  onRemover,
}: CardParticipanteProps) {
  const [justificativaAberta, setJustificativaAberta] = useState(false);
  const [menuAberto, setMenuAberto] = useState(false);
  const idJustificativa = useId();
  const idMenu = useId();
  const raizRef = useRef<HTMLDivElement>(null);
  const gatilhoRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // γ (ESC-DEC-006): para o membro comum o banco não libera o status dos colegas — sem selo.
  const selo =
    participante.respostaVisivel && participante.resposta
      ? rotuloResposta(participante.resposta, participante.origemResposta)
      : null;
  const justificativa =
    participante.respostaVisivel && participante.resposta === "RECUSADA" ? participante.justificativa : null;
  const temMenu = !!(onAlterarFuncao || onRegistrarResposta || onRemover);

  // Menu aberto: fecha ao clicar fora ou com Esc (devolvendo o foco ao gatilho) e foca o 1º item.
  useEffect(() => {
    if (!menuAberto) return;
    menuRef.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
    function aoClicarFora(e: MouseEvent) {
      if (raizRef.current && !raizRef.current.contains(e.target as Node)) setMenuAberto(false);
    }
    document.addEventListener("mousedown", aoClicarFora);
    return () => document.removeEventListener("mousedown", aoClicarFora);
  }, [menuAberto]);

  function aoTeclarNoMenu(e: KeyboardEvent<HTMLDivElement>) {
    const itens = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? []);
    const atual = itens.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") {
      e.preventDefault();
      setMenuAberto(false);
      gatilhoRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      itens[(atual + 1) % itens.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      itens[(atual - 1 + itens.length) % itens.length]?.focus();
    } else if (e.key === "Tab") {
      setMenuAberto(false);
    }
  }

  function escolher(acao?: () => void) {
    setMenuAberto(false);
    acao?.();
  }

  const classeItem =
    "flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 text-left text-sm font-semibold transition-colors focus:outline-none";
  const classeItemNormal = `${classeItem} text-text hover:bg-surface-2 focus:bg-surface-2`;
  const classeItemPerigo = `${classeItem} text-danger hover:bg-danger/10 focus:bg-danger/10`;

  return (
    <div ref={raizRef} className="relative flex flex-col gap-2 rounded-2xl border border-border bg-surface p-3">
      <div className="flex items-start gap-3">
        <Avatar nome={participante.nome} />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <p className="flex flex-wrap items-center gap-1.5 text-[15px] font-bold leading-tight text-text">
            <span className="break-words">{participante.nome}</span>
            {participante.ehPropria && (
              <span className="rounded-full bg-primary/10 px-1.5 py-px text-[10.5px] font-extrabold tracking-wide text-text">
                VOCÊ
              </span>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[13px] text-muted">{participante.funcaoNome ?? "Sem função"}</span>
            {selo && (
              <Pill tom={selo.tom} icone={selo.icone}>
                {selo.texto}
              </Pill>
            )}
          </div>
        </div>
        {temMenu && (
          <button
            ref={gatilhoRef}
            type="button"
            onClick={() => setMenuAberto((aberto) => !aberto)}
            aria-label={`Mais ações para ${participante.nome}`}
            aria-haspopup="menu"
            aria-expanded={menuAberto}
            aria-controls={menuAberto ? idMenu : undefined}
            className="-mr-1 -mt-1 grid h-11 w-11 shrink-0 place-items-center rounded-xl text-muted transition-colors hover:bg-surface-2 hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <EllipsisVertical size={20} aria-hidden />
          </button>
        )}
      </div>

      {justificativa && (
        <div className="pl-[52px]">
          <button
            type="button"
            onClick={() => setJustificativaAberta((aberta) => !aberta)}
            aria-expanded={justificativaAberta}
            aria-controls={justificativaAberta ? idJustificativa : undefined}
            className="inline-flex min-h-8 items-center gap-1.5 text-[12.5px] font-bold text-danger"
          >
            <MessageSquareText size={13} aria-hidden /> Motivo da recusa
            <ChevronDown size={13} aria-hidden className={justificativaAberta ? "rotate-180" : ""} />
          </button>
          {justificativaAberta && (
            <p
              id={idJustificativa}
              className="mt-0.5 break-words rounded-xl border border-danger/30 bg-danger/5 px-3 py-2.5 text-[13.5px] text-text"
            >
              “{justificativa}”
            </p>
          )}
        </div>
      )}

      {temMenu && menuAberto && (
        <div
          ref={menuRef}
          id={idMenu}
          role="menu"
          aria-label={`Ações para ${participante.nome}`}
          onKeyDown={aoTeclarNoMenu}
          className="absolute right-2.5 top-14 z-20 min-w-56 rounded-xl border border-border bg-surface p-1.5 shadow-xl"
        >
          {onAlterarFuncao && (
            <button type="button" role="menuitem" onClick={() => escolher(onAlterarFuncao)} className={classeItemNormal}>
              <Repeat size={18} aria-hidden className="text-muted" />
              Alterar função
            </button>
          )}
          {onRegistrarResposta && (
            <button type="button" role="menuitem" onClick={() => escolher(onRegistrarResposta)} className={classeItemNormal}>
              <ShieldCheck size={18} aria-hidden className="text-muted" />
              Registrar resposta
            </button>
          )}
          {onRemover && (
            <>
              {(onAlterarFuncao || onRegistrarResposta) && <hr className="mx-1.5 my-1 border-border" />}
              <button
                type="button"
                role="menuitem"
                onClick={() => escolher(onRemover)}
                className={classeItemPerigo}
              >
                <UserMinus size={18} aria-hidden />
                Remover da escala
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
